/** @jsxImportSource @emotion/react */
// see - https://www.figma.com/file/pv2HV6DTSKY612VWeYWYdL/CE22-Design-System?node-id=48%3A264
import { Divider } from '@mui/material';
import type { ReactNode } from 'react';
import React from 'react';
import type { Interpolation } from '@emotion/serialize';
import type { Theme } from '@emotion/react';
import Typography, {
  TypographyVariant,
} from '../../../component_library/components/Typography';
import type { ITheme } from '../../../component_library/styles/theme';
import { Link } from '../../../component_library/components/Link';
import { DataElementId } from '../../../constants/data-element-tracking-ids';
import { BoxWithId } from '../../../component_library/components/BoxWithId/BoxWithId';

interface ILink {
  path: string;
  label: string;
  icon: string;
}

interface IProps {
  firstSectionTitle: ReactNode;
  firstSectionLinks: ReadonlyArray<ILink>;
  firstSectionDivider?: boolean;
  secondSectionTitle: ReactNode;
  secondSectionLinks: ReadonlyArray<ILink>;
  secondSectionNew: boolean;
  thirdSectionTitle?: ReactNode;
  thirdSectionLinks?: ReadonlyArray<ILink>;
  theme: ITheme;
  additionalInfo?: ReactNode;
  additionalStyles: Interpolation<Theme>;
  handleOnLinkClick?: () => void;
}

const NavMobileList: React.FC<IProps> = ({
  firstSectionTitle,
  firstSectionLinks,
  firstSectionDivider,
  secondSectionTitle,
  secondSectionLinks,
  secondSectionNew,
  thirdSectionTitle,
  thirdSectionLinks,
  theme,
  additionalInfo,
  additionalStyles,
  handleOnLinkClick,
}) => {
  const rootStyles: Interpolation<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    flexGrow: '1',
    backgroundColor: theme.colors.grayscale.white,

    '& .nav-mobile-list-additional-section': {
      position: 'relative',
    },

    '& .nav-mobile-list-additional-section::before': {
      content: "''",
      position: 'absolute',
      top: '-20px',
      width: '100%',
      height: '1px',
      background: `${theme.colors.grayscale.lightGrey}`,
      overflow: 'hidden',
    },
  };
  const contentWrapperStyle = {
    marginBottom: '114px',
  };
  const linkStyles = {
    display: 'block',
    color: `${theme.colors.secondary}`,
    '&:not(:last-child)': {
      marginBottom: '28px',
    },
  };
  const linkIconStyles = {
    color: theme.colors.grayscale.inactiveGrey1,
    fontSize: '18px',
    marginRight: '8px',
    // Fixes weird bug where the fa-bar-chart icon is oriented the wrong way:
    '&.fa-bar-chart': {
      transform: 'rotate(90deg) scaleX(-1)',
    },
  };
  const firstSectionStyles = {
    marginBottom: '40px',
  };

  return (
    <BoxWithId
      css={[rootStyles, additionalStyles]}
      dataElementId={DataElementId.HEADER_NAV_MOBILE_LIST}
    >
      <div css={contentWrapperStyle}>
        <div css={firstSectionStyles}>
          <Typography
            variant={TypographyVariant.SMALL_EYEBROW}
            theme={theme}
            color={theme.colors.secondary}
            additionalStyles={{
              marginBottom: '24px',
            }}
          >
            {firstSectionTitle}
          </Typography>
          {firstSectionLinks?.map((link) => (
            <Link
              to={link.path}
              variant={TypographyVariant.MEDIUM_BODY_REGULAR}
              additionalStyles={linkStyles}
              color={theme.colors.secondary}
              key={link.label}
              dataElementId={DataElementId.HEADER_LINK_GENERIC}
              onClick={handleOnLinkClick}
            >
              <i className={link.icon} css={linkIconStyles} />
              {link.label}
            </Link>
          ))}
        </div>
        {firstSectionDivider && <Divider sx={{ marginBottom: '24px' }} />}
        <div
          css={
            thirdSectionTitle && thirdSectionLinks ? firstSectionStyles : {}
          }
        >
          <Typography
            variant={TypographyVariant.SMALL_EYEBROW}
            theme={theme}
            color={theme.colors.secondary}
            additionalStyles={{
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {secondSectionTitle}
            {secondSectionNew && (
              <div
                css={{
                  backgroundColor: theme.colors.positiveBackground,
                  color: theme.colors.positiveHover,
                  marginLeft: '8px',
                  padding: '6px 9px',
                }}
              >
                new
              </div>
            )}
          </Typography>
          {secondSectionLinks?.map((link) => (
            <Link
              to={link.path}
              variant={TypographyVariant.MEDIUM_BODY_REGULAR}
              additionalStyles={linkStyles}
              color={theme.colors.secondary}
              key={link.label}
              dataElementId={DataElementId.HEADER_LINK_GENERIC}
              onClick={handleOnLinkClick}
            >
              <i className={link.icon} css={linkIconStyles} />
              {link.label}
            </Link>
          ))}
        </div>
        {!!thirdSectionTitle && !!thirdSectionLinks && (
          <div>
            <Typography
              variant={TypographyVariant.SMALL_EYEBROW}
              theme={theme}
              color={theme.colors.secondary}
              additionalStyles={{
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {thirdSectionTitle}
            </Typography>
            {thirdSectionLinks?.map((link) => (
              <Link
                to={link.path}
                variant={TypographyVariant.MEDIUM_BODY_REGULAR}
                additionalStyles={linkStyles}
                color={theme.colors.secondary}
                key={link.label}
                dataElementId={DataElementId.HEADER_LINK_GENERIC}
                onClick={handleOnLinkClick}
              >
                <i className={link.icon} css={linkIconStyles} />
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </div>
      {!additionalInfo && <Divider variant="middle" />}
      {!!additionalInfo && (
        <div className="nav-mobile-list-additional-section">
          {additionalInfo}
        </div>
      )}
    </BoxWithId>
  );
};

export default NavMobileList;
